import { Component } from '@theme/component';
import { prefersReducedMotion, debounce, preloadImage } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
    this.#installDesktopStyles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = debounce(() => {
    if (!this.refs.dialog.open) return;
    this.#dialogMedia().forEach((item) => this.#positionAiBadge(item));
  }, 100);

  #installDesktopStyles() {
    if (document.getElementById('ab-product-zoom-fix')) return;
    const style = document.createElement('style');
    style.id = 'ab-product-zoom-fix';
    style.textContent = `
      @media screen and (min-width: 750px) {
        .dialog-zoomed-gallery__dialog { overflow: hidden !important; }
        .dialog-zoomed-gallery { height: 100vh !important; overflow: hidden !important; cursor: default !important; }
        .dialog-zoomed-gallery > .product-media-container {
          display: none !important;
          width: calc(100vw - 220px) !important;
          height: 100vh !important;
          margin: 0 !important;
          padding: 32px 32px 32px 40px !important;
          box-sizing: border-box !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .dialog-zoomed-gallery > .product-media-container.ab-zoom-active { display: flex !important; }
        .dialog-zoomed-gallery .product-media__drag-zoom-wrapper,
        .dialog-zoomed-gallery .product-media {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          overflow: hidden !important;
        }
        .dialog-zoomed-gallery .product-media__image {
          width: auto !important;
          height: auto !important;
          max-width: 78vw !important;
          max-height: 84vh !important;
          object-fit: contain !important;
          object-position: center !important;
          transform: none !important;
          cursor: zoom-out !important;
          border-radius: 18px !important;
          border: 1px solid rgba(255, 255, 255, 0.32) !important;
          box-shadow: 0 16px 42px rgba(0, 0, 0, 0.28) !important;
          box-sizing: border-box !important;
        }
        .dialog-thumbnails-list-container { pointer-events: none; }
        .dialog-thumbnails-list,
        .dialog-thumbnails-list__thumbnail { pointer-events: auto; }
      }
    `;
    document.head.appendChild(style);
  }

  #dialogMedia() {
    return Array.from(this.refs.dialog.querySelectorAll('.dialog-zoomed-gallery > .product-media-container'));
  }

  #thumbnailButtons() {
    return Array.from(this.refs.thumbnails.querySelectorAll('button.dialog-thumbnails-list__thumbnail'));
  }

  #positionAiBadge(mediaContainer) {
    requestAnimationFrame(() => {
      const wrapper = mediaContainer?.querySelector('.product-media');
      const image = mediaContainer?.querySelector('img.product-media__image');
      const badge = mediaContainer?.querySelector('.product-media__ai-badge');
      if (!(wrapper instanceof HTMLElement) || !(image instanceof HTMLImageElement) || !(badge instanceof HTMLElement)) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      const containerRect = mediaContainer.getBoundingClientRect();
      const thumbnailsContainer = this.refs.thumbnails.closest('.dialog-thumbnails-list-container');
      const thumbnailsRect = thumbnailsContainer?.getBoundingClientRect();
      let visibleRight = Math.min(imageRect.right, containerRect.right);
      let visibleBottom = Math.min(imageRect.bottom, containerRect.bottom);

      if (thumbnailsRect && thumbnailsRect.width >= window.innerWidth * 0.75 && thumbnailsRect.top > containerRect.top) {
        visibleBottom = Math.min(visibleBottom, thumbnailsRect.top - 56);
      }

      if (thumbnailsRect && thumbnailsRect.height >= window.innerHeight * 0.5 && thumbnailsRect.left > containerRect.left) {
        visibleRight = Math.min(visibleRight, thumbnailsRect.left);
      }

      const inset = 12;

      badge.style.left = `${Math.max(inset, visibleRight - wrapperRect.left - badge.offsetWidth - inset)}px`;
      badge.style.top = `${Math.max(inset, visibleBottom - wrapperRect.top - badge.offsetHeight - inset)}px`;
      badge.style.right = 'auto';
      badge.style.bottom = 'auto';
    });
  }

  #currentSourceIndex(event, fallbackIndex) {
    const sourceSlide = event?.target instanceof Element ? event.target.closest('slideshow-slide') : null;
    if (!sourceSlide) return fallbackIndex;

    const gallery = this.closest('media-gallery');
    if (!gallery) return fallbackIndex;

    const slides = Array.from(gallery.querySelectorAll('slideshow-component slideshow-slide'));
    const currentIndex = slides.indexOf(sourceSlide);
    return currentIndex >= 0 ? currentIndex : fallbackIndex;
  }

  #currentThumbnailIndex(event, fallbackIndex) {
    const clicked = event?.currentTarget instanceof Element
      ? event.currentTarget
      : event?.target instanceof Element
        ? event.target.closest('button.dialog-thumbnails-list__thumbnail')
        : null;

    if (!clicked) return fallbackIndex;

    const buttons = this.#thumbnailButtons();
    const currentIndex = buttons.indexOf(clicked);
    return currentIndex >= 0 ? currentIndex : fallbackIndex;
  }

  async open(index, event) {
    event.preventDefault();
    const currentIndex = this.#currentSourceIndex(event, index);
    this.refs.dialog.showModal();
    requestAnimationFrame(() => this.selectThumbnail(currentIndex, { behavior: 'instant' }));
  }

  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer?.classList.contains('product-media-container--image')) return false;
    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;
    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;
    preloadImage(highResolutionUrl);
    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
      this.#positionAiBadge(mediaContainer);
    };
    newImage.src = highResolutionUrl;
    return true;
  }

  handleScroll = debounce(async () => {
    if (window.matchMedia('(min-width: 750px)').matches) return;
    const media = this.#dialogMedia();
    const buttons = this.#thumbnailButtons();
    if (!media.length) return;
    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    buttons.forEach((button, i) => button.setAttribute('aria-selected', `${i === activeIndex}`));
    this.loadHighResolutionImage(mostVisibleElement);
    this.#positionAiBadge(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  async close() { this.closeDialog(); }

  closeDialog() {
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  async handleThumbnailClick(index, event) {
    const currentIndex = this.#currentThumbnailIndex(event, index);
    this.selectThumbnail(currentIndex, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  async handleThumbnailPointerEnter(index, event) {
    const currentIndex = this.#currentThumbnailIndex(event, index);
    const media = this.#dialogMedia();
    if (media[currentIndex]) this.loadHighResolutionImage(media[currentIndex]);
  }

  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const media = this.#dialogMedia();
    const buttons = this.#thumbnailButtons();
    if (isNaN(index) || index < 0 || index >= media.length || index >= buttons.length) return;

    const targetThumbnail = buttons[index];
    const targetImage = media[index];

    buttons.forEach((button, i) => button.setAttribute('aria-selected', `${i === index}`));

    scrollIntoView(targetThumbnail, {
      ancestor: this.refs.thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    if (window.matchMedia('(min-width: 750px)').matches) {
      media.forEach((item, i) => item.classList.toggle('ab-zoom-active', i === index));
    } else {
      targetImage.scrollIntoView({ behavior: options.behavior, block: 'nearest', inline: 'start' });
    }

    this.loadHighResolutionImage(targetImage);
    this.#positionAiBadge(targetImage);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);

function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver((entries) => {
      const mostVisible = entries.reduce((prev, current) => current.intersectionRatio > prev.intersectionRatio ? current : prev);
      observer.disconnect();
      resolve(/** @type {HTMLElement} */ (mostVisible.target));
    }, { threshold: Array.from({ length: 100 }, (_, i) => i / 100) });
    for (const element of elements) observer.observe(element);
  });
}
